<template>
  <div class="chat-sidebar" :class="{ 'sidebar-collapsed': isCollapsed }">
    <!-- Header -->
    <div class="sidebar-header">
      <div class="header-top">
        <h2 class="chat-title">Chats</h2>
        <div class="header-actions">
          <v-btn icon small @click="toggleSidebar" class="collapse-btn">
            <v-icon>{{ isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Search Section -->
    <div class="search-section">
      <div class="search-container">
        <div class="search-input-wrapper" :class="{ 'focused': searchFocused }">
          <v-icon class="search-icon" size="18">mdi-magnify</v-icon>
          <input v-model="searchKey" type="text" placeholder="Search or start new chat" class="search-input"
            @focus="searchFocused = true" @blur="searchFocused = false" @input="performSearch" />
          <v-btn v-if="searchKey" icon x-small class="clear-search" @click="clearSearch">
            <v-icon size="16">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-btn fab x-small class="new-chat-btn" @click="openNewChatDialog">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
        <v-btn fab x-small class="new-chat-btn" v-if="profileType == 'Agency'" @click="goToAgencyMessages">
          <v-icon>mdi-account-group</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Filter Tabs -->
    <div class="filter-tabs" ref="filterTabs">
      <div class="tabs-container">
        <div v-for="tab in filterTabs" :key="tab.key" class="tab-item" :class="{ 'tab-active': activeTab === tab.key }"
          @click="setActiveTab(tab.key)">
          <v-icon :size="18" class="tab-icon">{{ tab.icon }}</v-icon>
          <span class="tab-label">{{ tab.label }}</span>
          <v-chip v-if="getTabCount(tab.key) > 0" x-small :color="activeTab === tab.key ? 'white' : 'primary'"
            :text-color="activeTab === tab.key ? 'primary' : 'white'" class="tab-count">
            {{ getTabCount(tab.key) }}
          </v-chip>
        </div>
      </div>
    </div>

    <!-- Chat List -->
    <div class="chat-list-container">
      <div v-if="loading" class="loading-state">
        <v-progress-circular indeterminate color="primary" size="32" />
        <span class="loading-text">Loading conversations...</span>
      </div>

      <div v-else-if="!filteredChats || filteredChats.length === 0" class="empty-state">
        <v-icon size="56" color="grey lighten-2">{{ getEmptyStateIcon() }}</v-icon>
        <h4 class="empty-title">{{ getEmptyStateTitle() }}</h4>
        <p class="empty-subtitle">{{ getEmptyStateSubtitle() }}</p>
      </div>

      <div v-else class="chat-list">
        <div v-for="chat in filteredChats" :key="chat._id" class="chat-item" :class="{
          'chat-item-active': isActiveChat(chat),
          'chat-item-unread': hasUnreadMessages(chat),
          'chat-item-pinned': chat.isPinned
        }" @click="selectChat(chat)" @contextmenu.prevent="showChatMenu($event, chat)">
          <div class="chat-avatar-container">
            <editable-avatar :image="getChatAvatar(chat)" :size="48" :editable="false" class="chat-avatar" />
            <div class="online-status" v-if="isUserOnline(chat)"></div>
          </div>

          <div class="chat-info">
            <div class="chat-header">
              <span class="chat-name">
                {{ truncateName(getChatDisplayName(chat), 20) }}
                <v-icon v-if="chat.isPinned" size="12" class="pin-icon" color="orange">mdi-pin</v-icon>
              </span>
              <span class="chat-time">{{ formatMessageTime(chat) }}</span>
            </div>

            <div class="chat-preview">
              <div class="last-message">
                <v-icon v-if="isLastMessageFromMe(chat) && !isUserTypingInChat(chat)" size="14"
                  :color="getMessageStatusColor(chat)" class="message-status">
                  {{ getMessageStatusIcon(chat) }}
                </v-icon>

                <div v-if="isUserTypingInChat(chat)" class="typing-indicator">
                  <span class="dot"></span>
                  <span class="dot"></span>
                  <span class="dot"></span>
                  <span class="typing-text">typing...</span>
                </div>

                <span v-else class="message-text" :class="{ 'message-unread': hasUnreadMessages(chat) }">
                  {{ getLastMessageText(chat) }}
                </span>
              </div>

              <div class="chat-indicators">
                <v-icon v-if="chat.isMuted" size="14" class="mute-icon" color="grey">mdi-volume-off</v-icon>
                <div v-if="hasUnreadMessages(chat)" class="unread-indicator">
                  {{ getUnreadCount(chat) > 99 ? "99+" : getUnreadCount(chat) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FAB Button -->
    <div class="floating-actions">
      <v-speed-dial v-model="fab" direction="top" transition="slide-y-reverse-transition">
        <template v-slot:activator>
          <v-btn v-model="fab" fab dark large color="primary" class="fab-main-btn">
            <v-icon v-if="fab" size="24">mdi-close</v-icon>
            <v-icon v-else size="24">mdi-plus</v-icon>
          </v-btn>
        </template>
        <v-btn fab dark small color="success" @click="openNewChatDialog">
          <v-icon>mdi-message-text</v-icon>
        </v-btn>
        <v-btn fab dark small color="info" @click="openCreateGroupDialog">
          <v-icon>mdi-account-group</v-icon>
        </v-btn>
      </v-speed-dial>
    </div>

    <!-- New Chat Dialog -->
    <v-dialog v-model="newChatDialog" max-width="500" persistent :fullscreen="$vuetify.breakpoint.xs">
      <v-card class="rounded-xl">
        <v-card-title class="dialog-header">
          <v-icon left dark>mdi-message-plus</v-icon>
          New Chat
          <v-spacer></v-spacer>
          <v-btn icon dark @click="newChatDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-text-field v-model="chatSearchKey" label="Search for users..." prepend-inner-icon="mdi-magnify" outlined
            clearable @input="searchForNewChat" autofocus :loading="searching" />

          <!-- Loading State -->
          <div v-if="searching" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="40" />
            <p class="grey--text mt-2">Searching for users...</p>
          </div>

          <!-- Users List -->
          <div v-else-if="chatSearchResults && chatSearchResults.length > 0" class="user-search-results">
            <v-list>
              <v-list-item v-for="user in chatSearchResults" :key="user._id || user.id" @click="startNewChat(user)"
                class="user-search-item" :disabled="creatingChat">
                <v-list-item-avatar>
                  <editable-avatar :editable="false" :image="getUserAvatar(user)" :size="40" />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ getUserFullName(user) }}</v-list-item-title>
                  <v-list-item-subtitle>{{ getUserType(user) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon small color="primary" :loading="creatingChat && selectedUser?._id === user._id">
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

          <!-- No Results -->
          <div v-else-if="chatSearchKey && chatSearchKey.length > 2" class="text-center py-4">
            <v-icon size="48" color="grey lighten-2">mdi-account-search</v-icon>
            <p class="grey--text mt-2">No users found for "{{ chatSearchKey }}"</p>
          </div>

          <!-- Initial State -->
          <div v-else class="text-center py-4">
            <v-icon size="48" color="grey lighten-2">mdi-chat-plus</v-icon>
            <p class="grey--text mt-2">Search for users to start a new chat</p>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="newChatDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Group Dialog -->
    <v-dialog v-model="createGroupDialog" max-width="600" persistent :fullscreen="$vuetify.breakpoint.xs">
      <v-card class="rounded-xl">
        <v-card-title class="dialog-header info">
          <v-icon left dark>mdi-account-group-plus</v-icon>
          Create New Group
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeCreateGroupDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <div class="group-image-upload">
            <div class="group-avatar-wrapper" @click="triggerImageUpload">
              <editable-avatar :editable="false" :image="groupImagePreview || require('@/assets/default.jpg')"
                :size="80" class="group-avatar" />
              <div class="group-avatar-overlay">
                <v-icon color="white" size="24">mdi-camera</v-icon>
              </div>
            </div>
            <input type="file" ref="groupImageInput" accept="image/*" style="display: none"
              @change="handleGroupImageUpload" />
          </div>

          <v-text-field v-model="groupName" label="Group Name *" outlined prepend-inner-icon="mdi-rename-box"
            :rules="[v => !!v || 'Group name is required']" required />

          <v-textarea v-model="groupDescription" label="Group Description" outlined
            prepend-inner-icon="mdi-text-box-outline" rows="2" counter="200" maxlength="200" />

          <v-text-field v-model="groupSearchKey" label="Search members to add..."
            prepend-inner-icon="mdi-account-search" outlined clearable @input="searchForGroupMembers"
            :loading="searching" />

          <div v-if="selectedMembers && selectedMembers.length > 0" class="selected-members">
            <span class="caption grey--text">Selected Members ({{ selectedMembers.length }})</span>
            <div class="member-chips">
              <v-chip v-for="member in selectedMembers" :key="member._id || member.id" close
                @click:close="removeMember(member)" color="primary" text-color="white" class="member-chip">
                <editable-avatar :editable="false" :image="getUserAvatar(member)" :size="20"
                  class="member-chip-avatar" />
                {{ getUserFullName(member) }}
              </v-chip>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="searching" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="40" />
            <p class="grey--text mt-2">Loading users...</p>
          </div>

          <!-- Group Search Results -->
          <div v-else-if="groupSearchResults && groupSearchResults.length > 0" class="user-search-results">
            <v-list>
              <v-list-item v-for="user in groupSearchResults" :key="user._id || user.id" @click="addMember(user)"
                class="user-search-item" :disabled="isMemberSelected(user)">
                <v-list-item-avatar>
                  <editable-avatar :editable="false" :image="getUserAvatar(user)" :size="40" />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ getUserFullName(user) }}</v-list-item-title>
                  <v-list-item-subtitle>{{ getUserType(user) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon small :color="isMemberSelected(user) ? 'grey' : 'success'">
                    <v-icon>{{ isMemberSelected(user) ? 'mdi-check' : 'mdi-plus' }}</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

          <!-- No Results -->
          <div v-else-if="groupSearchKey && groupSearchKey.length > 2" class="text-center py-4">
            <v-icon size="48" color="grey lighten-2">mdi-account-search</v-icon>
            <p class="grey--text mt-2">No users found</p>
          </div>

          <!-- Initial State -->
          <div v-else-if="!groupSearchKey" class="text-center py-4">
            <v-icon size="48" color="grey lighten-2">mdi-account-plus</v-icon>
            <p class="grey--text mt-2">Search for users to add to the group</p>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateGroupDialog">Cancel</v-btn>
          <v-btn color="info" @click="createGroup" :loading="creatingGroup"
            :disabled="!groupName || !selectedMembers || selectedMembers.length < 2 || creatingGroup">
            Create Group
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Chat Confirmation Dialog -->
    <v-dialog v-model="deleteChatDialog" max-width="420" persistent>
      <v-card class="rounded-xl">
        <v-card-text class="pt-6 pb-3 text-center">
          <v-icon color="error" size="64" class="mb-2">mdi-delete-forever</v-icon>
          <h2 class="mb-2 text-h5 font-weight-bold">Delete Chat?</h2>
          <p class="text-body-1 grey--text">
            {{ deleteChatType === 'group'
              ? 'This will remove you from the group and delete the chat for you.'
              : 'This will delete the chat for you. The other person will still see the chat.'
            }}
          </p>
          <p v-if="deleteChatType === 'group' && isGroupOwner" class="text-body-2 red--text">
            ⚠️ You are the group owner. Deleting will transfer ownership to another member.
          </p>
          <p class="text-body-2 grey--text mt-2">
            This action cannot be undone.
          </p>
        </v-card-text>
        <div class="px-4 pb-3">
          <v-btn block large depressed color="error" dark class="mb-3 rounded-pill" @click="confirmDeleteChat"
            :loading="deletingChat">
            <v-icon left>mdi-delete</v-icon>
            {{ deleteChatType === 'group' ? 'Leave & Delete' : 'Delete Chat' }}
          </v-btn>
          <v-btn block text large class="rounded-lg text-capitalize grey--text text--darken-1"
            @click="deleteChatDialog = false">
            Cancel
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Context Menu -->
    <v-menu v-model="contextMenu.show" :position-x="contextMenu.x" :position-y="contextMenu.y" absolute offset-y>
      <v-list dense>
        <!-- Pin/Unpin option -->
        <v-list-item @click="togglePinChat(contextMenu.chat)">
          <v-list-item-icon>
            <v-icon :color="contextMenu.chat && contextMenu.chat.isPinned ? 'orange' : ''">
              {{ contextMenu.chat && contextMenu.chat.isPinned ? 'mdi-pin' : 'mdi-pin-outline' }}
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            {{ contextMenu.chat && contextMenu.chat.isPinned ? 'Unpin chat' : 'Pin chat' }}
          </v-list-item-title>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item v-if="contextMenu.chat && hasUnreadMessages(contextMenu.chat)"
          @click="markAsRead(contextMenu.chat)">
          <v-list-item-icon><v-icon>mdi-check-all</v-icon></v-list-item-icon>
          <v-list-item-title>Mark as read</v-list-item-title>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item @click="openDeleteChatDialog(contextMenu.chat)" class="error--text">
          <v-list-item-icon><v-icon color="error">mdi-delete</v-icon></v-list-item-icon>
          <v-list-item-title>Delete chat</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :timeout="4000" bottom :color="snackbarColor">
      <v-icon left>{{ snackbarIcon }}</v-icon>
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import EditableAvatar from "../../components/EditableAvatar.vue";
import socket from "@/socket";

export default {
  name: "ChatMenuSection",
  components: { EditableAvatar },

  data() {
    return {
      searchKey: "",
      searchFocused: false,
      searchResults: [],
      searching: false,
      searchLimit: 10,
      searchPage: 1,
      activeTab: "all",
      loading: false,
      fab: false,
      isCollapsed: false,

      // New Chat
      newChatDialog: false,
      chatSearchKey: "",
      chatSearchResults: [],
      chatSearchPage: 1,
      chatSearchLimit: 10,
      creatingChat: false,
      selectedUser: null,

      // Create Group
      createGroupDialog: false,
      groupName: "",
      groupDescription: "",
      groupImage: null,
      groupImagePreview: null,
      groupSearchKey: "",
      groupSearchResults: [],
      groupSearchPage: 1,
      groupSearchLimit: 10,
      selectedMembers: [],
      creatingGroup: false,

      // Snackbar
      snackbar: false,
      snackMessage: "",
      snackbarIcon: "mdi-information",
      snackbarColor: "info",

      // Typing
      typingUsers: {},
      typingTimeouts: {},

      deleteChatDialog: false,
      deletingChat: false,
      chatToDelete: null,

      // Context Menu
      contextMenu: {
        show: false,
        x: 0,
        y: 0,
        chat: null
      },

      filterTabs: [
        { key: "all", label: "All", icon: "mdi-message" },
        { key: "groups", label: "Groups", icon: "mdi-account-group" },
        { key: "agencies", label: "Agencies", icon: "mdi-office-building" },
        { key: "volunteers", label: "Volunteers", icon: "mdi-account-heart" },
        { key: "unread", label: "Unread", icon: "mdi-message-badge" }
      ],

      // Socket handler references
      newMessageHandler: null,
      messageDeliveredHandler: null,
      messagesReadHandler: null,
      messageDeletedHandler: null,
      messageDeletedForEveryoneHandler: null,
      reactionUpdatedHandler: null,
      chatPinnedHandler: null,
      messagePinnedHandler: null,
      userOnlineHandler: null,
      userOfflineHandler: null,
      typingStartHandler: null,
      typingStopHandler: null,
      cleanupInterval: null
    };
  },

  computed: {
    ...mapGetters({
      chatRooms: "chat/getConversations",
      getChats: "chat/getChats",
      profileType: "auth/getType",
      getSingleRoom: "chat/getSingleRoom"
    }),
    ...mapState("auth", {
      profile: "profile"
    }),

    activeChat() {
      return this.getSingleRoom || null;
    },

    deleteChatType() {
      return this.chatToDelete?.type || 'individual';
    },

    isGroupOwner() {
      if (!this.chatToDelete || this.chatToDelete.type !== 'group') return false;
      return this.chatToDelete.createdBy?._id === this.profile?._id;
    },

    filteredChats() {
      let chats = this.getChats || [];

      if (!chats || !Array.isArray(chats)) {
        return [];
      }

      switch (this.activeTab) {
        case "groups":
          chats = chats.filter(chat => chat && chat.type === "group");
          break;
        case "agencies":
          chats = chats.filter(chat => chat && this.getChatUserType(chat) === "Agency");
          break;
        case "volunteers":
          chats = chats.filter(chat => chat && this.getChatUserType(chat) === "Volunteer");
          break;
        case "unread":
          chats = chats.filter(chat => chat && this.getUnreadCount(chat) > 0);
          break;
        default:
          break;
      }

      if (this.searchKey) {
        const searchLower = this.searchKey.toLowerCase();
        chats = chats.filter(chat => {
          if (!chat) return false;
          const displayName = this.getChatDisplayName(chat).toLowerCase();
          const lastMessage = this.getLastMessageText(chat).toLowerCase();
          return displayName.includes(searchLower) || lastMessage.includes(searchLower);
        });
      }

      return chats.sort((a, b) => {
        if (!a || !b) return 0;
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        const aTime = this.getLastMessageTime(a);
        const bTime = this.getLastMessageTime(b);
        return new Date(bTime) - new Date(aTime);
      });
    },
  },

  methods: {
    ...mapActions("chat", {
      searchUsers: "searchUsers",
      getAllChats: "getAllChats",
      getChatById: "getChatById",
      markMessageAsRead: "markMessageAsRead",
      clearChat: "clearChat",
      createGroupChat: "createGroupChat",
      sendMessage: "sendMessage",
      pinChat: "pinChat",
      deleteChatForMe: "deleteChatForMe"
    }),

    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },

    // ========== SOCKET METHODS ==========
    setupSocketListeners() {
      this.cleanupSocketListeners();

      // New message
      this.newMessageHandler = (data) => {
        this.handleSocketNewMessage(data);
      };
      socket.on('new-message', this.newMessageHandler);

      // Message delivered
      this.messageDeliveredHandler = (data) => {
        this.handleSocketMessageDelivered(data);
      };
      socket.on('message-delivered', this.messageDeliveredHandler);

      // Messages read
      this.messagesReadHandler = (data) => {
        this.handleSocketMessagesRead(data);
      };
      socket.on('messages-read', this.messagesReadHandler);

      // Message deleted
      this.messageDeletedHandler = (data) => {
        this.handleSocketMessageDeleted(data);
      };
      socket.on('message-deleted', this.messageDeletedHandler);

      // Message deleted for everyone
      this.messageDeletedForEveryoneHandler = (data) => {
        this.handleSocketMessageDeletedForEveryone(data);
      };
      socket.on('message-deleted-for-everyone', this.messageDeletedForEveryoneHandler);

      // Reaction updated
      this.reactionUpdatedHandler = (data) => {
        this.handleSocketReactionUpdated(data);
      };
      socket.on('reaction-updated', this.reactionUpdatedHandler);

      // Chat pinned
      this.chatPinnedHandler = (data) => {
        this.handleSocketChatPinned(data);
      };
      socket.on('chat-pinned', this.chatPinnedHandler);

      // Message pinned
      this.messagePinnedHandler = (data) => {
        this.handleSocketMessagePinned(data);
      };
      socket.on('message-pinned', this.messagePinnedHandler);

      this.chatUpdatedHandler = (data) => this.handleSocketChatUpdated(data);
      socket.on("chat-updated", this.chatUpdatedHandler);

      // User online/offline
      this.userOnlineHandler = (data) => {
        this.handleUserOnline(data);
      };
      socket.on('user-online', this.userOnlineHandler);

      this.userOfflineHandler = (data) => {
        this.handleUserOffline(data);
      };
      socket.on('user-offline', this.userOfflineHandler);

      // Typing
      this.typingStartHandler = (data) => {
        this.handleTypingStart(data);
      };
      socket.on('typing-start', this.typingStartHandler);

      this.typingStopHandler = (data) => {
        this.handleTypingStop(data);
      };
      socket.on('typing-stop', this.typingStopHandler);
    },

    cleanupSocketListeners() {
      if (this.newMessageHandler) {
        socket.off('new-message', this.newMessageHandler);
        this.newMessageHandler = null;
      }
      if (this.messageDeliveredHandler) {
        socket.off('message-delivered', this.messageDeliveredHandler);
        this.messageDeliveredHandler = null;
      }
      if (this.messagesReadHandler) {
        socket.off('messages-read', this.messagesReadHandler);
        this.messagesReadHandler = null;
      }
      if (this.messageDeletedHandler) {
        socket.off('message-deleted', this.messageDeletedHandler);
        this.messageDeletedHandler = null;
      }
      if (this.messageDeletedForEveryoneHandler) {
        socket.off('message-deleted-for-everyone', this.messageDeletedForEveryoneHandler);
        this.messageDeletedForEveryoneHandler = null;
      }
      if (this.reactionUpdatedHandler) {
        socket.off('reaction-updated', this.reactionUpdatedHandler);
        this.reactionUpdatedHandler = null;
      }
      if (this.chatUpdatedHandler) {
        socket.off("chat-updated", this.chatUpdatedHandler);
        this.chatUpdatedHandler = null;
      }
      if (this.chatPinnedHandler) {
        socket.off('chat-pinned', this.chatPinnedHandler);
        this.chatPinnedHandler = null;
      }
      if (this.messagePinnedHandler) {
        socket.off('message-pinned', this.messagePinnedHandler);
        this.messagePinnedHandler = null;
      }
      if (this.userOnlineHandler) {
        socket.off('user-online', this.userOnlineHandler);
        this.userOnlineHandler = null;
      }
      if (this.userOfflineHandler) {
        socket.off('user-offline', this.userOfflineHandler);
        this.userOfflineHandler = null;
      }
      if (this.typingStartHandler) {
        socket.off('typing-start', this.typingStartHandler);
        this.typingStartHandler = null;
      }
      if (this.typingStopHandler) {
        socket.off('typing-stop', this.typingStopHandler);
        this.typingStopHandler = null;
      }
    },

    handleSocketNewMessage(data) {
      const { chatId, message } = data;
      if (!chatId || !message) return;

      const chats = this.getChats;
      if (!chats || !Array.isArray(chats)) return;

      const chatIndex = chats.findIndex(c => c && c._id === chatId);
      if (chatIndex !== -1) {
        if (!chats[chatIndex].messages) {
          chats[chatIndex].messages = [];
        }
        chats[chatIndex].lastMessage = message;
        chats[chatIndex].lastActivityAt = message.createdAt || new Date();

        if (message.sender?._id !== this.profile?._id) {
          if (!chats[chatIndex].unreadCount) {
            chats[chatIndex].unreadCount = {};
          }
          chats[chatIndex].unreadCount[this.profile?._id] =
            (chats[chatIndex].unreadCount[this.profile?._id] || 0) + 1;
        }

        const chat = chats.splice(chatIndex, 1)[0];
        chats.unshift(chat);

        this.$store.commit('chat/UPDATE_CHAT', chats[0]);
        this.$forceUpdate();
      } else {
        this.getAllChats();
      }
    },

    handleSocketChatUpdated(data) {
  const { chatId, lastMessage, lastActivityAt, unreadCount } = data;
  if (!chatId) return;

  const chats = this.getChats || [];
  const chat = chats.find(c => c && c._id === chatId);
  if (!chat) return;

  if (lastMessage !== undefined) chat.lastMessage = lastMessage;
  if (lastActivityAt !== undefined) chat.lastActivityAt = lastActivityAt;
  if (unreadCount !== undefined) {
    chat.currentUserUnreadCount = unreadCount;
    if (!chat.unreadCount) chat.unreadCount = {};
    chat.unreadCount[this.profile?._id] = unreadCount;
  }

  this.$store.commit("chat/UPDATE_CHAT", chat);
  this.$forceUpdate();
},

    handleSocketMessageDelivered(data) {
      const { chatId, messageId, deliveredTo } = data;
      if (!chatId || !messageId) return;

      const chat = this.getChats.find(c => c && c._id === chatId);
      if (chat && chat.messages) {
        const message = chat.messages.find(m => m && m._id === messageId);
        if (message) {
          message.delivered = true;
          message.status = 'delivered';
          if (deliveredTo) {
            message.deliveredTo = deliveredTo;
          }
        }
      }
    },

    handleSocketMessagesRead(data) {
      const { chatId, userId, lastReadSequence } = data;
      if (!chatId || !userId) return;

      const chat = this.getChats.find(c => c && c._id === chatId);
      if (chat && chat.messages) {
        chat.messages.forEach(message => {
          if (message && message.sequenceNumber <= lastReadSequence &&
            message.sender?._id !== userId) {
            message.read = true;
            message.status = 'read';
          }
        });
      }

      if (chat && chat.unreadCount) {
        if (chat.unreadCount[userId]) {
          chat.unreadCount[userId] = 0;
        }
      }
    },

    handleSocketMessageDeleted(data) {
      const { chatId, messageId } = data;
      if (!chatId || !messageId) return;

      const chat = this.getChats.find(c => c && c._id === chatId);
      if (chat && chat.messages) {
        chat.messages = chat.messages.filter(m => m && m._id !== messageId);
        if (chat.messages.length === 0) {
          chat.lastMessage = null;
        } else {
          chat.lastMessage = chat.messages[chat.messages.length - 1];
        }
        this.$forceUpdate();
      }
    },

    handleSocketMessageDeletedForEveryone(data) {
      const { chatId, messageId } = data;
      if (!chatId || !messageId) return;

      const chat = this.getChats.find(c => c && c._id === chatId);
      if (chat && chat.messages) {
        const message = chat.messages.find(m => m && m._id === messageId);
        if (message) {
          message.isDeleted = true;
          message.text = 'This message was deleted';
          message.attachments = [];
          message.messageType = 'system';
          this.$forceUpdate();
        }
      }
    },

    handleSocketReactionUpdated(data) {
      const { chatId, messageId, emoji, user, action } = data;
      if (!chatId || !messageId) return;

      const chat = this.getChats.find(c => c && c._id === chatId);
      if (chat && chat.messages) {
        const message = chat.messages.find(m => m && m._id === messageId);
        if (message) {
          if (!message.reactions) {
            message.reactions = [];
          }

          if (action === 'add') {
            const existing = message.reactions.find(r => r.emoji === emoji);
            if (existing) {
              if (!existing.users.find(u => u._id === user._id)) {
                existing.users.push(user);
              }
            } else {
              message.reactions.push({ emoji, users: [user] });
            }
          } else if (action === 'remove') {
            const reactionIndex = message.reactions.findIndex(r => r.emoji === emoji);
            if (reactionIndex !== -1) {
              const reaction = message.reactions[reactionIndex];
              reaction.users = reaction.users.filter(u => u._id !== user._id);
              if (reaction.users.length === 0) {
                message.reactions.splice(reactionIndex, 1);
              }
            }
          }
          this.$forceUpdate();
        }
      }
    },

    handleSocketChatPinned(data) {
      const { chatId, pinned } = data;
      if (!chatId) return;

      const chat = this.getChats.find(c => c && c._id === chatId);
      if (chat) {
        chat.isPinned = pinned;
        this.$store.commit('chat/TOGGLE_PIN_CHAT', { chatId, isPinned: pinned });
        this.$forceUpdate();
      }
    },

    handleSocketMessagePinned(data) {
      const { chatId, messageId, pinned } = data;
      if (!chatId || !messageId) return;

      const chat = this.getChats.find(c => c && c._id === chatId);
      if (chat && chat.messages) {
        const message = chat.messages.find(m => m && m._id === messageId);
        if (message) {
          message.isPinned = pinned;
          this.$forceUpdate();
        }
      }
    },

    handleUserOnline(data) {
      const { userId } = data;
      if (!userId) return;

      this.getChats.forEach(chat => {
        if (chat && chat.participants) {
          const participant = chat.participants.find(p => p && p._id === userId);
          if (participant) {
            participant.isOnline = true;
          }
        }
      });
    },

    handleUserOffline(data) {
      const { userId } = data;
      if (!userId) return;

      this.getChats.forEach(chat => {
        if (chat && chat.participants) {
          const participant = chat.participants.find(p => p && p._id === userId);
          if (participant) {
            participant.isOnline = false;
          }
        }
      });
    },

    // ========== CHAT LIST METHODS ==========
    getChatDisplayName(chat) {
      if (!chat) return "Unknown Chat";
      if (chat.type === "group") return chat.name || "Group Chat";
      if (chat.type === "support") return chat.supportMeta?.displayName || "Support";
      if (chat.type === "individual") {
        const opposite = this.getOppositeParticipant(chat);
        if (opposite) {
          return this.getUserFullName(opposite);
        }
        return "Unknown User";
      }
      return "Unknown Chat";
    },

    getOppositeParticipant(chat) {
      if (!chat || !chat.participants || !Array.isArray(chat.participants)) {
        if (chat && chat.selectedUser) {
          return chat.selectedUser;
        }
        return null;
      }

      const profileId = this.profile?._id;
      if (!profileId) return chat.participants[0] || null;

      const opposite = chat.participants.find(p => {
        if (!p) return false;
        const pId = p._id || p.userID || p.id;
        return String(pId) !== String(profileId);
      });

      if (!opposite) {
        return chat.participants[0] || null;
      }

      if (opposite && !opposite.name && !opposite.first_name) {
        opposite.name = opposite.email ? opposite.email.split('@')[0] : 'User';
      }

      return opposite;
    },

   getChatAvatar(chat) {
  if (!chat) return require("@/assets/default.jpg");

  if (chat.selectedUser) {
    return this.getUserAvatar(chat.selectedUser);
  }

  if (chat.type === "group" && chat.groupImage) return chat.groupImage;
  if (chat.type === "support" && chat.supportMeta?.displayAvatar) {
    return chat.supportMeta.displayAvatar;
  }
  if (chat.type === "individual") {
    const opposite = this.getOppositeParticipant(chat);
    if (opposite) return this.getUserAvatar(opposite);
  }
  return require("@/assets/default.jpg");
},

    getChatUserType(chat) {
      if (!chat) return "User";
      if (chat.type === "group") return "Group";
      if (chat.type === "support") return "Support";
      const opposite = this.getOppositeParticipant(chat);
      return this.getUserType(opposite);
    },

    getUnreadCount(chat) {
      if (!chat) return 0;
      if (chat.currentUserUnreadCount !== undefined) return chat.currentUserUnreadCount;
      if (chat.unreadCount && this.profile?._id) {
        return chat.unreadCount[this.profile._id] || 0;
      }
      return 0;
    },

    getLastMessageText(chat) {
      if (!chat) return "No messages yet";

      // Check if there's a lastMessage property
      if (chat.lastMessage) {
        let text = "";
        if (chat.lastMessage.text) text = chat.lastMessage.text;
        else if (chat.lastMessage.message) text = chat.lastMessage.message;
        else if (chat.lastMessage.messageType === 'image') text = '📷 Photo';
        else if (chat.lastMessage.messageType === 'video') text = '🎬 Video';
        else if (chat.lastMessage.messageType === 'file') text = '📎 File';
        return text.length > 40 ? text.slice(0, 40) + "..." : text || "No messages yet";
      }

      // Fallback to messages array
      const messages = chat.messages || chat.recentMessages || [];
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage) return "No messages yet";

      let text = "";
      if (lastMessage.text) text = lastMessage.text;
      else if (lastMessage.message) text = lastMessage.message;
      else if (Array.isArray(lastMessage.images) && lastMessage.images.length > 0) {
        text = lastMessage.images.length > 1 ? `📸 ${lastMessage.images.length} Photos` : `📸 Photo`;
      } else if (lastMessage.messageType === 'image') {
        text = '📷 Photo';
      } else if (lastMessage.messageType === 'video') {
        text = '🎬 Video';
      } else if (lastMessage.messageType === 'file') {
        text = '📎 File';
      }

      return text.length > 40 ? text.slice(0, 40) + "..." : text || "No messages yet";
    },

    getLastMessageTime(chat) {
      if (!chat) return new Date();
      return chat.lastActivityAt || chat.updatedAt || new Date();
    },

    formatMessageTime(chat) {
      const timestamp = this.getLastMessageTime(chat);
      if (!timestamp) return "now";
      const date = new Date(timestamp);
      const now = new Date();
      const diffInMs = now - date;
      const diffInHours = diffInMs / (1000 * 60 * 60);

      if (diffInHours < 1) return "now";
      if (diffInHours < 24) return `${Math.floor(diffInHours)}h`;
      if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d`;
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    },

    isLastMessageFromMe(chat) {
      if (!chat) return false;
      if (chat.lastMessage) {
        return chat.lastMessage.senderID === this.profile?._id ||
          chat.lastMessage.fromUser === this.profile?._id;
      }
      const messages = chat.messages || chat.recentMessages || [];
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage) return false;
      return lastMessage.senderID === this.profile?._id || lastMessage.fromUser === this.profile?._id;
    },

    getMessageStatusIcon(chat) {
      if (!chat) return "";
      const lastMessage = chat.lastMessage || chat.messages?.[chat.messages?.length - 1];
      if (!lastMessage || lastMessage.senderID !== this.profile?._id) return "";
      if (lastMessage.isRead || lastMessage.read) return "mdi-check-all";
      if (lastMessage.isDelivered) return "mdi-check";
      return "mdi-clock-outline";
    },

    getMessageStatusColor(chat) {
      if (!chat) return "grey";
      const lastMessage = chat.lastMessage || chat.messages?.[chat.messages?.length - 1];
      if (!lastMessage) return "grey";
      if (lastMessage.isRead || lastMessage.read) return "blue";
      if (lastMessage.isDelivered) return "grey";
      return "grey lighten-1";
    },

    isUserOnline(chat) {
      const opposite = this.getOppositeParticipant(chat);
      return opposite?.isOnline || false;
    },

      isUserTypingInChat(chat) {
      if (!chat?._id) return false;
      return !!this.typingUsers[chat._id];
    },

    isActiveChat(chat) {
      return this.$route.query.chatId === chat?._id;
    },

    hasUnreadMessages(chat) {
      return this.getUnreadCount(chat) > 0;
    },

    getEmptyStateIcon() {
      switch (this.activeTab) {
        case "groups": return "mdi-account-group-outline";
        case "agencies": return "mdi-office-building-outline";
        case "volunteers": return "mdi-account-heart-outline";
        case "unread": return "mdi-message-badge-outline";
        default: return "mdi-chat-outline";
      }
    },

    getEmptyStateTitle() {
      switch (this.activeTab) {
        case "groups": return "No group conversations";
        case "agencies": return "No agency conversations";
        case "volunteers": return "No volunteer conversations";
        case "unread": return "No unread messages";
        default: return "No conversations yet";
      }
    },

    getEmptyStateSubtitle() {
      switch (this.activeTab) {
        case "groups": return "Create a group or join existing ones";
        case "agencies": return "Start a conversation with an agency";
        case "volunteers": return "Connect with volunteers";
        case "unread": return "All caught up!";
        default: return "Search for users to start chatting";
      }
    },

    getTabCount(tab) {
      const chats = this.getChats || [];
      if (!Array.isArray(chats)) return 0;
      switch (tab) {
        case "all": return chats.filter(c => c).length;
        case "groups": return chats.filter(chat => chat && chat.type === "group").length;
        case "agencies": return chats.filter(chat => chat && this.getChatUserType(chat) === "Agency").length;
        case "volunteers": return chats.filter(chat => chat && this.getChatUserType(chat) === "Volunteer").length;
        case "unread": return chats.filter(chat => chat && this.getUnreadCount(chat) > 0).length;
        default: return 0;
      }
    },

    setActiveTab(tab) {
      this.activeTab = tab;
    },

    clearSearch() {
      this.searchKey = "";
      this.searchResults = [];
      this.searching = false;
      this.searchPage = 1;
    },

    async performSearch() {
      // This is handled by the filteredChats computed property
    },

    async selectChat(chat) {
      try {
        if (!chat) return;
        if (this.$route.query.chatId === chat._id) return;

        let selectedChat = chat;
        if (chat._id) {
          try {
            const freshChat = await this.getChatById(chat._id);
            if (freshChat) selectedChat = freshChat;
          } catch (error) {
            console.warn("Could not refresh chat data:", error);
          }
        }

        if (!selectedChat.messages) selectedChat.messages = [];
        selectedChat.messages = selectedChat.messages.filter(message => message !== null) || [];

        this.$store.commit("chat/setSingleRoom", selectedChat);

        await this.$router.replace({
          name: "chat-details",
          query: { chatId: selectedChat._id }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationAborted') {
            console.error('Navigation error:', err);
          }
        });
      } catch (error) {
        console.error("Error selecting chat:", error);
        this.showSnackbar("Failed to open chat", "error", "mdi-alert");
      }
    },

    showChatMenu(event, chat) {
      event.preventDefault();
      this.contextMenu = {
        show: true,
        x: event.clientX,
        y: event.clientY,
        chat
      };
    },
// In ChatMenuSection.vue - replace the markAsRead method

markAsRead(chat) {
  if (!chat) {
    this.showSnackbar("Chat not found", "error", "mdi-alert");
    return;
  }

  this.contextMenu.show = false;
  
  // Use the correct payload structure expected by the backend
  const payload = {
    chatId: chat._id,
    lastReadSequence: chat.lastSequence || 0
  };
  
  // Call the Vuex action
  this.markMessageAsRead(payload)
    .then(() => {
      // Update local state
      if (chat && chat.messages) {
        chat.messages.forEach(message => {
          if (message && message.senderID !== this.profile?._id) {
            message.isRead = true;
            message.read = true;
          }
        });
      }
      
      // Reset unread count
      if (chat && chat.unreadCount) {
        chat.unreadCount[this.profile?._id] = 0;
      }
      
      // Update Vuex store
      this.$store.commit('chat/UPDATE_CHAT', chat);
      this.$forceUpdate();
      
      this.showSnackbar("Messages marked as read", "success", "mdi-check-all");
    })
    .catch((error) => {
      console.error("Error marking messages as read:", error);
      this.showSnackbar("Failed to mark messages as read", "error", "mdi-alert");
    });
},

    // ========== USER HELPER METHODS ==========
    getUserId(user) {
      if (!user) return null;
      return user._id || user.id || null;
    },

    getUserAvatar(user) {
      if (!user) return require("@/assets/default.jpg");
      return user.midThumbnail || user.thumbnail || user.avatar || user.image || require("@/assets/default.jpg");
    },

    getUserType(user) {
      if (!user) return "User";
      if (user.userType) return user.userType;
      if (user.type) return user.type;
      if (user.role) return user.role;
      if (user.companyName) return "Agency";
      if (user.agencyName) return "Agency";
      return "User";
    },

    getUserFullName(user) {
      if (!user) return "Unknown User";

      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`.trim();
      }
      if (user.first_name) return user.first_name;
      if (user.last_name) return user.last_name;
      if (user.name) return user.name;
      if (user.fullName) return user.fullName;
      if (user.displayName) return user.displayName;
      if (user.email) return user.email.split('@')[0];
      if (this.getUserId(user)) return "User";
      return "Unknown User";
    },

    // ========== PIN CHAT ==========
    async togglePinChat(chat) {
      if (!chat) {
        this.showSnackbar("Chat not found", "error", "mdi-alert");
        return;
      }

      this.contextMenu.show = false;

      try {
        const result = await this.pinChat({ chatId: chat._id });

        if (result && result.success) {
          this.$store.commit("chat/TOGGLE_PIN_CHAT", {
            chatId: chat._id,
            isPinned: result.pinned
          });

          this.showSnackbar(
            result.pinned ? "Chat pinned to top" : "Chat unpinned",
            "success",
            result.pinned ? "mdi-pin" : "mdi-pin-off"
          );
        } else {
          this.showSnackbar(result?.message || "Failed to update pin status", "error", "mdi-alert");
        }
      } catch (error) {
        console.error("Error toggling pin:", error);
        this.showSnackbar(error.message || "Failed to update pin status", "error", "mdi-alert");
      }
    },

    // ========== DELETE CHAT ==========
    openDeleteChatDialog(chat) {
      if (!chat) {
        this.showSnackbar("Chat not found", "error", "mdi-alert");
        return;
      }

      this.contextMenu.show = false;
      this.chatToDelete = chat;
      this.deleteChatDialog = true;
    },

    async confirmDeleteChat() {
      if (!this.chatToDelete) {
        this.showSnackbar("Chat not found", "error", "mdi-alert");
        return;
      }

      this.deletingChat = true;

      try {
        const payload = {
          chatId: this.chatToDelete._id,
          hardDelete: false
        };

        if (this.chatToDelete.type === 'group') {
          const activeParticipants = this.chatToDelete.participants?.filter(p => p.isActive !== false) || [];
          if (activeParticipants.length === 1) {
            payload.hardDelete = true;
          }
        }

        const response = await this.$store.dispatch("chat/deleteChatForMe", payload);

        if (response && response.success) {
          if (this.isActiveChat(this.chatToDelete)) {
            this.$store.commit("chat/setSingleRoom", null);
            const remainingChats = this.filteredChats.filter(c => c && c._id !== this.chatToDelete._id);
            if (remainingChats.length > 0) {
              await this.selectChat(remainingChats[0]);
            } else {
              await this.$router.push({ name: "chat-details", query: {} });
            }
          }

          this.showSnackbar(
            this.chatToDelete.type === 'group' ? "You have left and deleted the group chat" : "Chat deleted successfully",
            "success",
            "mdi-check"
          );

          this.deleteChatDialog = false;
          this.chatToDelete = null;
        } else {
          throw new Error(response?.message || "Failed to delete chat");
        }
      } catch (error) {
        console.error("Error deleting chat:", error);
        this.showSnackbar(error.message || "Failed to delete chat. Please try again.", "error", "mdi-alert");
      } finally {
        this.deletingChat = false;
      }
    },

    // ========== TYPING HANDLERS ==========
       handleTypingStart(data) {
      const chatId = data.chatId || data.roomID;
      const fromUser = data.fromUser || data.userId;
      if (!chatId || !fromUser) return;
      if (String(fromUser) === String(this.profile?._id)) return;

      this.$set(this.typingUsers, chatId, {
        userId: fromUser,
        timestamp: Date.now()
      });

      if (this.typingTimeouts[chatId]) {
        clearTimeout(this.typingTimeouts[chatId]);
      }

      this.typingTimeouts[chatId] = setTimeout(() => {
        this.$delete(this.typingUsers, chatId);
        delete this.typingTimeouts[chatId];
      }, 5000);
    },

    handleTypingStop(data) {
      const chatId = data.chatId || data.roomID;
      if (!chatId) return;
      this.$delete(this.typingUsers, chatId);
      if (this.typingTimeouts[chatId]) {
        clearTimeout(this.typingTimeouts[chatId]);
        delete this.typingTimeouts[chatId];
      }
    },

    // ========== NEW CHAT ==========
    openNewChatDialog() {
      this.fab = false;
      this.chatSearchKey = "";
      this.chatSearchResults = [];
      this.chatSearchPage = 1;
      this.creatingChat = false;
      this.selectedUser = null;
      this.newChatDialog = true;

      this.$nextTick(() => {
        this.loadAllUsersForChat();
      });
    },

    async loadAllUsersForChat() {
      try {
        this.searching = true;
        const response = await this.searchUsers({
          search: '',
          page: 1,
          limit: 20
        });

        let users = [];
        if (Array.isArray(response)) {
          users = response;
        } else if (response?.res && Array.isArray(response.res)) {
          users = response.res;
        } else if (response?.data && Array.isArray(response.data)) {
          users = response.data;
        } else {
          users = [];
        }

        this.chatSearchResults = users.filter(user => {
          const userId = this.getUserId(user);
          return userId && userId !== this.profile?._id;
        });
      } catch (error) {
        console.error("Error loading users:", error);
        this.chatSearchResults = [];
        this.showSnackbar("Failed to load users", "warning", "mdi-alert");
      } finally {
        this.searching = false;
      }
    },

    async searchForNewChat() {
      if (!this.chatSearchKey || this.chatSearchKey.trim() === '') {
        await this.loadAllUsersForChat();
        return;
      }

      if (this.chatSearchKey.length < 3) {
        await this.loadAllUsersForChat();
        const searchLower = this.chatSearchKey.toLowerCase();
        this.chatSearchResults = this.chatSearchResults.filter(user => {
          const fullName = this.getUserFullName(user).toLowerCase();
          const email = (user.email || '').toLowerCase();
          return fullName.includes(searchLower) || email.includes(searchLower);
        });
        return;
      }

      try {
        this.searching = true;
        const payload = {
          search: this.chatSearchKey,
          page: this.chatSearchPage,
          limit: this.chatSearchLimit
        };
        const response = await this.searchUsers(payload);

        let results = [];
        if (Array.isArray(response)) {
          results = response;
        } else if (response?.res && Array.isArray(response.res)) {
          results = response.res;
        } else if (response?.data && Array.isArray(response.data)) {
          results = response.data;
        } else {
          results = [];
        }

        this.chatSearchResults = results.filter(user => {
          const userId = this.getUserId(user);
          return userId && userId !== this.profile?._id;
        });
      } catch (error) {
        console.error("Search error:", error);
        await this.loadAllUsersForChat();
        const searchLower = this.chatSearchKey.toLowerCase();
        this.chatSearchResults = this.chatSearchResults.filter(user => {
          const fullName = this.getUserFullName(user).toLowerCase();
          return fullName.includes(searchLower);
        });
      } finally {
        this.searching = false;
      }
    },

   async startNewChat(user) {
  if (!user) return;

  const userId = this.getUserId(user);

  // If a chat with this user already exists, open it instead of creating a new one
  const existingChat = (this.getChats || []).find(chat => {
    if (!chat || chat.type !== "individual") return false;

    if (chat.selectedUser) {
      const selId = this.getUserId(chat.selectedUser);
      if (selId && String(selId) === String(userId)) return true;
    }

    if (Array.isArray(chat.participants)) {
      return chat.participants.some(p => {
        const pId = p && (p._id || p.userID || p.id);
        return pId && String(pId) === String(userId);
      });
    }

    return false;
  });

  this.newChatDialog = false;

  if (existingChat) {
    await this.selectChat(existingChat);
    return;
  }

  // Normalize the user so the real avatar field (midThumbnail/thumbnail/avatar) is resolved
  const normalizedUser = {
    ...user,
    _id: userId,
    userID: userId,
    userType: this.getUserType(user),
    name: this.getUserFullName(user),
    image: this.getUserAvatar(user)
  };

  const tempId = `temp-${Date.now()}`;
  const tempChat = {
    _id: tempId,
    id: tempId,
    type: "individual",
    participants: [normalizedUser],
    selectedUser: normalizedUser,
    createdAt: new Date().toISOString(),
    lastActivityAt: new Date().toISOString(),
    lastMessage: null,
    hasMessages: false,
    status: "draft",
    messages: []
  };

  this.$store.commit("chat/ADD_CHAT", tempChat);
  this.$store.commit("chat/setSingleRoom", tempChat);

  await this.$router.replace({
    name: "chat-details",
    query: { chatId: tempId }
  }).catch(err => {
    if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationAborted') {
      console.error('Navigation error:', err);
    }
  });
},

    // ========== CREATE GROUP ==========
    openCreateGroupDialog() {
      this.fab = false;
      this.resetGroupForm();
      this.createGroupDialog = true;

      this.$nextTick(() => {
        this.loadAllUsersForGroup();
      });
    },

    async loadAllUsersForGroup() {
      try {
        this.searching = true;
        const response = await this.searchUsers({
          search: '',
          page: 1,
          limit: 20
        });

        let users = [];
        if (Array.isArray(response)) {
          users = response;
        } else if (response?.res && Array.isArray(response.res)) {
          users = response.res;
        } else if (response?.data && Array.isArray(response.data)) {
          users = response.data;
        } else {
          users = [];
        }

        this.groupSearchResults = users
          .filter(user => {
            const userId = this.getUserId(user);
            if (!user.userType && !user.type) {
              user.userType = "User";
            }
            return userId && userId !== this.profile?._id && !this.isMemberSelected(user);
          })
          .map(user => ({
            ...user,
            userType: user.userType || user.type || "User"
          }));
      } catch (error) {
        console.error("Error loading users:", error);
        this.groupSearchResults = [];
        this.showSnackbar("Failed to load users", "warning", "mdi-alert");
      } finally {
        this.searching = false;
      }
    },

    resetGroupForm() {
      this.groupName = "";
      this.groupDescription = "";
      this.groupImage = null;
      this.groupImagePreview = null;
      this.groupSearchKey = "";
      this.groupSearchResults = [];
      this.groupSearchPage = 1;
      this.selectedMembers = [];
      this.creatingGroup = false;
    },

    closeCreateGroupDialog() {
      this.createGroupDialog = false;
      this.resetGroupForm();
    },

    triggerImageUpload() {
      this.$refs.groupImageInput.click();
    },

    handleGroupImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          this.showSnackbar("Please select an image file", "warning", "mdi-alert");
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          this.showSnackbar("Image size should be less than 5MB", "warning", "mdi-alert");
          return;
        }

        this.groupImage = file;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.groupImagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
        event.target.value = '';
      }
    },

    async searchForGroupMembers() {
      if (!this.groupSearchKey || this.groupSearchKey.trim() === '') {
        await this.loadAllUsersForGroup();
        return;
      }

      if (this.groupSearchKey.length < 3) {
        await this.loadAllUsersForGroup();
        const searchLower = this.groupSearchKey.toLowerCase();
        this.groupSearchResults = this.groupSearchResults.filter(user => {
          const fullName = this.getUserFullName(user).toLowerCase();
          return fullName.includes(searchLower);
        });
        return;
      }

      try {
        this.searching = true;
        const payload = {
          search: this.groupSearchKey,
          page: this.groupSearchPage,
          limit: this.groupSearchLimit
        };
        const response = await this.searchUsers(payload);

        let results = [];
        if (Array.isArray(response)) {
          results = response;
        } else if (response?.res && Array.isArray(response.res)) {
          results = response.res;
        } else if (response?.data && Array.isArray(response.data)) {
          results = response.data;
        } else {
          results = [];
        }

        this.groupSearchResults = results
          .filter(user => {
            const userId = this.getUserId(user);
            if (!user.userType && !user.type) {
              user.userType = "User";
            }
            return userId && userId !== this.profile?._id && !this.isMemberSelected(user);
          })
          .map(user => ({
            ...user,
            userType: user.userType || user.type || "User"
          }));
      } catch (error) {
        console.error("Search error:", error);
        await this.loadAllUsersForGroup();
        const searchLower = this.groupSearchKey.toLowerCase();
        this.groupSearchResults = this.groupSearchResults.filter(user => {
          const fullName = this.getUserFullName(user).toLowerCase();
          return fullName.includes(searchLower);
        });
      } finally {
        this.searching = false;
      }
    },

    addMember(user) {
      if (user && !this.isMemberSelected(user)) {
        const memberWithType = {
          ...user,
          userType: this.getUserType(user)
        };
        this.selectedMembers.push(memberWithType);
        this.groupSearchResults = this.groupSearchResults.filter(u => this.getUserId(u) !== this.getUserId(user));
        this.groupSearchKey = "";
      }
    },

    removeMember(member) {
      if (member) {
        this.selectedMembers = this.selectedMembers.filter(m => this.getUserId(m) !== this.getUserId(member));
      }
    },

    isMemberSelected(user) {
      if (!user) return false;
      const userId = this.getUserId(user);
      return this.selectedMembers.some(m => this.getUserId(m) === userId);
    },

  async createGroup() {
  if (!this.groupName || !this.selectedMembers || this.selectedMembers.length < 2) {
    this.showSnackbar("Please provide a group name and select at least 2 members", "warning", "mdi-alert");
    return;
  }

  this.creatingGroup = true;
  try {
    const participantIds = this.selectedMembers.map(member => ({
      _id: this.getUserId(member),
      userType: this.getUserType(member)
    }));

    if (participantIds.length < 2) {
      throw new Error("Please select at least 2 valid members");
    }

    const createParams = {
      chatName: this.groupName.trim(),
      chatType: "group",
      chatDescription: this.groupDescription?.trim() || "",
      participantIds: participantIds
    };

    let result;
    if (this.groupImage) {
      const formData = new FormData();
      formData.append("chatName", this.groupName.trim());
      formData.append("chatType", "group");
      formData.append("chatDescription", this.groupDescription?.trim() || "");
      formData.append("participantIds", JSON.stringify(participantIds));
      formData.append("groupImage", this.groupImage);

      result = await this.createGroupChat(formData);
    } else {
      result = await this.createGroupChat(createParams);
    }

    // Close the dialog first
    this.closeCreateGroupDialog();

    // Extract the group chat from the response
    let groupChat = result;
    
    // Handle different response structures
    if (result && result.res) {
      groupChat = result.res;
    } else if (result && result.data) {
      groupChat = result.data;
    }

    if (groupChat && groupChat._id) {
      // Add the chat to the store first
      this.$store.commit("chat/ADD_CHAT", groupChat);
      
      // Set as active room
      this.$store.commit("chat/setSingleRoom", groupChat);

      // Navigate to the chat
      await this.$router.replace({
        name: "chat-details",
        query: { chatId: groupChat._id }
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationAborted') {
          console.error('Navigation error:', err);
        }
      });

      // Fetch the full chat data to ensure all messages are loaded
      try {
        const freshChat = await this.getChatById(groupChat._id);
        if (freshChat) {
          this.$store.commit("chat/setSingleRoom", freshChat);
        }
      } catch (fetchError) {
        console.warn("Could not fetch fresh chat data:", fetchError);
        // The group chat we have is still valid
      }

      this.showSnackbar("Group created successfully!", "success", "mdi-check");
    } else {
      throw new Error("Invalid group response - no chat ID received");
    }
  } catch (error) {
    console.error("Error creating group:", error);
    this.showSnackbar(error.message || "Failed to create group. Please try again.", "error", "mdi-alert");
    // Re-open the dialog if creation failed
    this.createGroupDialog = true;
  } finally {
    this.creatingGroup = false;
  }
},

async navigateToChat(chatId) {
  if (!chatId) return;
  
  try {
    // First, try to get the fresh chat data
    const freshChat = await this.getChatById(chatId);
    if (freshChat) {
      this.$store.commit("chat/setSingleRoom", freshChat);
    }
    
    // Navigate to the chat
    await this.$router.replace({
      name: "chat-details",
      query: { chatId: chatId }
    }).catch(err => {
      if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationAborted') {
        console.error('Navigation error:', err);
      }
    });
  } catch (error) {
    console.error("Error navigating to chat:", error);
    // Fallback: try to navigate with the chat ID
    await this.$router.replace({
      name: "chat-details",
      query: { chatId: chatId }
    }).catch(err => {
      if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationAborted') {
        console.error('Navigation error:', err);
      }
    });
  }
},

    goToAgencyMessages() {
      this.$router.push({ name: "agency-message" });
    },

    // ========== SNACKBAR ==========
    showSnackbar(message, color = "info", icon = "mdi-information") {
      this.snackMessage = message;
      this.snackbarColor = color;
      this.snackbarIcon = icon;
      this.snackbar = true;
    },

    truncateName(name, length = 30) {
      if (!name) return "";
      return name.length > length ? name.slice(0, length) + "..." : name;
    },

    cleanupTypingIndicators() {
      const now = Date.now();
      Object.entries(this.typingUsers).forEach(([roomId, data]) => {
        if (now - data.timestamp > 10000) {
          this.$delete(this.typingUsers, roomId);
          if (this.typingTimeouts[roomId]) {
            clearTimeout(this.typingTimeouts[roomId]);
            delete this.typingTimeouts[roomId];
          }
        }
      });
    }
  },

  watch: {
    searchKey: {
      handler: "performSearch",
      immediate: false
    },
    '$route.query.chatId': {
      handler(newVal) {
        if (!newVal && this.filteredChats && this.filteredChats.length > 0) {
          this.selectChat(this.filteredChats[0]);
        }
      },
      immediate: true
    }
  },

  mounted() {
    this.typingUsers = {};
    this.typingTimeouts = {};

    this.setupSocketListeners();

    if (this.profile?._id) {
      socket.emit('join-chat', { chatId: `user_${this.profile._id}` });
    }

    this.cleanupInterval = setInterval(this.cleanupTypingIndicators, 5000);

    this.loading = true;
    this.getAllChats()
      .then(() => {
        if (!this.$route.query.chatId && this.filteredChats && this.filteredChats.length > 0) {
          this.selectChat(this.filteredChats[0]);
        }
      })
      .catch((error) => {
        console.error("Error loading chats:", error);
        this.showSnackbar("Failed to load conversations", "error", "mdi-alert");
      })
      .finally(() => {
        this.loading = false;
      });
  },

  beforeDestroy() {
    this.cleanupSocketListeners();

    if (this.profile?._id) {
      socket.emit('leave-chat', { chatId: `user_${this.profile._id}` });
    }

    if (this.cleanupInterval) clearInterval(this.cleanupInterval);
    Object.values(this.typingTimeouts).forEach(timeout => clearTimeout(timeout));
    this.typingTimeouts = {};
    this.typingUsers = {};
  }
};
</script>

<style scoped>
/* ===== MAIN SIDEBAR ===== */
.chat-sidebar {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 380px;
  min-width: 320px;
  max-width: 420px;
  background: #ffffff;
  border-right: 1px solid #e4e6ea;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
}

.chat-sidebar.sidebar-collapsed {
  width: 60px;
  min-width: 60px;
  max-width: 60px;
}

.chat-sidebar.sidebar-collapsed .search-section,
.chat-sidebar.sidebar-collapsed .filter-tabs,
.chat-sidebar.sidebar-collapsed .chat-list-container,
.chat-sidebar.sidebar-collapsed .chat-title,
.chat-sidebar.sidebar-collapsed .floating-actions {
  display: none;
}

/* ===== SIDEBAR HEADER ===== */
.sidebar-header {
  padding: 16px 20px 8px;
  background: #ffffff;
  border-bottom: 1px solid #e4e6ea;
  flex-shrink: 0;
}

.header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-title {
  font-size: 20px;
  font-weight: 700;
  color: #111b21;
  margin: 0;
}

.collapse-btn {
  color: #667781 !important;
}

/* ===== SEARCH SECTION ===== */
.search-section {
  padding: 12px 16px;
  background: #ffffff;
  border-bottom: 1px solid #e4e6ea;
  flex-shrink: 0;
}

.search-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  background: #f0f2f5;
  border-radius: 20px;
  padding: 6px 12px;
  gap: 8px;
  flex: 1;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.search-input-wrapper.focused {
  background: #ffffff;
  border-color: #084468;
  box-shadow: 0 0 0 4px rgba(8, 68, 104, 0.1);
}

.search-icon {
  color: #54656f;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #111b21;
  min-width: 0;
}

.search-input::placeholder {
  color: #8696a0;
}

.clear-search {
  color: #8696a0 !important;
  flex-shrink: 0;
}

.new-chat-btn {
  background: #084468 !important;
  color: white !important;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(8, 68, 104, 0.3);
}

.new-chat-btn:hover {
  background: #0a5a8a !important;
}

/* ===== FILTER TABS ===== */
.filter-tabs {
  padding: 8px 12px;
  background: #ffffff;
  border-bottom: 1px solid #e4e6ea;
  flex-shrink: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #c1c7cd transparent;
}

.filter-tabs::-webkit-scrollbar {
  height: 3px;
}

.filter-tabs::-webkit-scrollbar-track {
  background: transparent;
}

.filter-tabs::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.tabs-container {
  display: flex;
  gap: 4px;
  min-width: max-content;
  padding: 2px 0;
}

.tab-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  font-size: 13px;
  background: transparent;
  border: 1px solid transparent;
}

.tab-item:hover {
  background: #f0f2f5;
}

.tab-item.tab-active {
  background: #084468;
  color: white;
  border-color: #084468;
}

.tab-item.tab-active .tab-icon {
  color: white;
}

.tab-item.tab-active .tab-label {
  color: white;
}

.tab-icon {
  opacity: 0.7;
  font-size: 18px;
}

.tab-item.tab-active .tab-icon {
  opacity: 1;
}

.tab-label {
  font-size: 13px;
  font-weight: 500;
  color: #41525d;
}

.tab-count {
  font-size: 10px !important;
  min-width: 18px !important;
  height: 18px !important;
  padding: 0 6px !important;
  font-weight: 600 !important;
}

/* ===== CHAT LIST ===== */
.chat-list-container {
  flex: 1;
  overflow: hidden;
  background: #ffffff;
}

.chat-list {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 0;
}

.chat-list::-webkit-scrollbar {
  width: 4px;
}

.chat-list::-webkit-scrollbar-track {
  background: transparent;
}

.chat-list::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.chat-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  gap: 12px;
  position: relative;
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);
}

.chat-item:hover {
  background: #f7f9fa;
}

.chat-item-active {
  background: #e8f0fe;
}

.chat-item-active:hover {
  background: #dce8fa;
}

.chat-item-unread {
  background: rgba(8, 68, 104, 0.04);
}

.chat-item-unread:hover {
  background: rgba(8, 68, 104, 0.07);
}

.chat-item-pinned {
  background: rgba(255, 193, 7, 0.04);
}

.chat-avatar-container {
  position: relative;
  flex-shrink: 0;
}

.chat-avatar {
  border-radius: 50%;
}

.online-status {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: #31a24c;
  border-radius: 50%;
  border: 2px solid #ffffff;
}

.chat-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-name {
  font-size: 15px;
  font-weight: 500;
  color: #111b21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 4px;
}

.pin-icon {
  flex-shrink: 0;
}

.chat-time {
  font-size: 12px;
  color: #8696a0;
  white-space: nowrap;
  flex-shrink: 0;
}

.chat-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.last-message {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.message-text {
  font-size: 14px;
  color: #667781;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-unread {
  color: #111b21;
  font-weight: 500;
}

.message-status {
  flex-shrink: 0;
}

.chat-indicators {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.unread-indicator {
  background: #084468;
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== TYPING INDICATOR ===== */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 3px;
}

.typing-indicator .dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #667781;
  animation: typingPulse 1.4s infinite ease-in-out;
}

.typing-indicator .dot:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-indicator .dot:nth-child(2) {
  animation-delay: -0.16s;
}

.typing-indicator .dot:nth-child(3) {
  animation-delay: 0s;
}

.typing-text {
  font-size: 12px;
  color: #667781;
  font-style: italic;
  margin-left: 4px;
}

@keyframes typingPulse {

  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }

  40% {
    opacity: 1;
    transform: scale(1);
  }
}

/* ===== FAB BUTTON ===== */
.floating-actions {
  position: absolute;
  bottom: 24px;
  right: 20px;
  z-index: 10;
}

.fab-main-btn {
  box-shadow: 0 4px 12px rgba(8, 68, 104, 0.4) !important;
}

/* ===== EMPTY STATE ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
}

.empty-title {
  font-size: 18px;
  font-weight: 500;
  color: #41525d;
  margin: 12px 0 8px;
}

.empty-subtitle {
  font-size: 14px;
  color: #8696a0;
  margin: 0;
}

/* ===== LOADING STATE ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
}

.loading-text {
  color: #8696a0;
  font-size: 14px;
}

/* ===== DIALOG STYLES ===== */
.dialog-header {
  background: linear-gradient(135deg, #084468, #3b7ca1);
  color: white;
  padding: 16px 24px;
}

.dialog-header.info {
  background: linear-gradient(135deg, #0d47a1, #1976d2);
}

.user-search-results {
  max-height: 300px;
  overflow-y: auto;
  margin-top: 8px;
}

.user-search-item {
  cursor: pointer;
  border-radius: 8px;
  margin: 2px 0;
}

.user-search-item:hover {
  background: #f0f2f5;
}

/* ===== GROUP CREATION ===== */
.group-image-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
}

.group-avatar-wrapper {
  position: relative;
  cursor: pointer;
}

.group-avatar-wrapper:hover .group-avatar-overlay {
  opacity: 1;
}

.group-avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.selected-members {
  margin: 12px 0;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 8px;
  max-height: 120px;
  overflow-y: auto;
}

.member-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.member-chip {
  display: flex;
  align-items: center;
  gap: 4px;
}

.member-chip-avatar {
  margin-right: 4px;
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 1024px) {
  .chat-sidebar {
    width: 340px;
    min-width: 280px;
    max-width: 380px;
  }
}

@media (max-width: 768px) {
  .chat-sidebar {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    height: 100vh;
  }

  .chat-sidebar.sidebar-collapsed {
    width: 60px;
    min-width: 60px;
  }

  .search-section {
    padding: 10px 12px;
  }

  .filter-tabs {
    padding: 6px 8px;
  }

  .tab-item {
    padding: 4px 10px;
    font-size: 12px;
  }

  .tab-label {
    font-size: 12px;
  }

  .tab-icon {
    font-size: 16px;
  }

  .chat-item {
    padding: 8px 12px;
  }

  .chat-name {
    font-size: 14px;
  }

  .message-text {
    font-size: 13px;
  }
}

@media (max-width: 600px) {
  .tab-label {
    font-size: 12px;
  }

  .tab-item {
    padding: 4px 10px;
    font-size: 12px;
  }

  .tab-icon {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .chat-sidebar {
    min-width: 100%;
  }

  .sidebar-header {
    padding: 12px 14px 6px;
  }

  .chat-title {
    font-size: 18px;
  }

  .search-input {
    font-size: 13px;
  }

  .filter-tabs {
    padding: 4px 8px;
  }

  .tab-item {
    padding: 3px 8px;
    font-size: 11px;
  }

  .tab-label {
    font-size: 11px;
  }

  .tab-icon {
    font-size: 14px;
  }

  .tab-count {
    font-size: 9px !important;
    min-width: 16px !important;
    height: 16px !important;
    padding: 0 4px !important;
  }

  .chat-item {
    padding: 6px 10px;
    gap: 8px;
  }

  .chat-avatar {
    width: 40px !important;
    height: 40px !important;
  }

  .online-status {
    width: 10px;
    height: 10px;
  }

  .chat-name {
    font-size: 13px;
  }

  .chat-time {
    font-size: 10px;
  }

  .message-text {
    font-size: 12px;
  }

  .unread-indicator {
    min-width: 16px;
    height: 16px;
    font-size: 9px;
    padding: 0 4px;
  }

  .floating-actions {
    bottom: 16px;
    right: 12px;
  }

  .fab-main-btn {
    width: 48px !important;
    height: 48px !important;
  }
}

/* ===== DARK MODE ===== */
.theme--dark .chat-sidebar {
  background: #111b21;
  border-right-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .sidebar-header {
  background: #111b21;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .chat-title {
  color: #e9edef;
}

.theme--dark .search-section {
  background: #111b21;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .search-input-wrapper {
  background: #2a3942;
}

.theme--dark .search-input-wrapper.focused {
  background: #2a3942;
  border-color: #084468;
}

.theme--dark .search-input {
  color: #e9edef;
}

.theme--dark .filter-tabs {
  background: #111b21;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .tab-item:hover {
  background: #2a3942;
}

.theme--dark .tab-item.tab-active {
  background: #084468;
}

.theme--dark .tab-label {
  color: #8696a0;
}

.theme--dark .chat-list-container {
  background: #111b21;
}

.theme--dark .chat-item:hover {
  background: #182229;
}

.theme--dark .chat-item-active {
  background: rgba(8, 68, 104, 0.15);
}

.theme--dark .chat-item-unread {
  background: rgba(8, 68, 104, 0.1);
}

.theme--dark .chat-name {
  color: #e9edef;
}

.theme--dark .message-text {
  color: #8696a0;
}

.theme--dark .message-unread {
  color: #e9edef;
}

.theme--dark .empty-title {
  color: #e9edef;
}

.theme--dark .selected-members {
  background: #1a2630;
}

.theme--dark .user-search-item:hover {
  background: #182229;
}
</style>